import type { CSSProperties } from "react";
import { RadioButton } from "primereact/radiobutton";

export interface ChoiceCardOption<T extends string> {
  value: T;
  label: string;
  /** Una línea que explica qué pasa al elegir la opción. */
  description: string;
  /** Clase de PrimeIcons, ej. "pi pi-ticket". */
  icon: string;
}

interface ChoiceCardsProps<T extends string> {
  /** Base de los ids de las tarjetas (y `name` de los radios). */
  name: string;
  options: ChoiceCardOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Columnas de la grilla; igual que el <FormRow columns> donde vive. */
  columns?: number;
}

/**
 * Opción única en tarjetas: ícono, título y una línea de descripción, con el
 * radio a la derecha como indicador. Toda la tarjeta es clickeable y se elige
 * también con teclado (Tab + Enter/Espacio). El clic lo maneja la tarjeta: un
 * <label> apuntando al RadioButton de PrimeReact no dispara su onChange.
 */
export const ChoiceCards = <T extends string>({
  name,
  options,
  value,
  onChange,
  disabled = false,
  columns = 2,
}: ChoiceCardsProps<T>) => (
  <div
    className="choice-cards"
    role="radiogroup"
    style={{ "--choice-columns": columns } as CSSProperties}
  >
    {options.map((option) => {
      const selected = option.value === value;
      const select = () => {
        if (!disabled && !selected) onChange(option.value);
      };
      return (
        <div
          key={option.value}
          id={`${name}-${option.value}`}
          role="radio"
          aria-checked={selected}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onClick={select}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              select();
            }
          }}
          className={[
            "choice-card",
            selected ? "choice-card--selected" : "",
            disabled ? "choice-card--disabled" : "",
          ].join(" ")}
        >
          <span className="choice-card__icon" aria-hidden="true">
            <i className={option.icon} />
          </span>
          <span className="choice-card__text">
            <span className="font-semibold text-900">{option.label}</span>
            <span className="text-sm text-600 line-height-3">{option.description}</span>
          </span>
          {/* Solo indicador: la selección la maneja la tarjeta. */}
          <RadioButton
            name={name}
            value={option.value}
            checked={selected}
            disabled={disabled}
            tabIndex={-1}
            aria-hidden="true"
            className="pointer-events-none"
          />
        </div>
      );
    })}
  </div>
);
